import { inject, Injectable, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IReleaseNote } from '../interfaces/IReleaseNote';
import { ReleaseNotesService } from '../services/ReleaseNotesService';
import { ReleaseNoteDisplayFrequency } from '../enums/ReleaseNoteDisplayFrequency';
import { ReleaseNoteDisplayKind } from '../enums/ReleaseNoteDisplayKind';

@Injectable({
  providedIn: 'root'
})
export class ReleaseNotesStore {

  private static readonly SessionStorageKey = 'release-note-session-dismissed';

  private readonly _releaseNotesService = inject(ReleaseNotesService);

  public readonly banners = signal<IReleaseNote[]>([]);
  public readonly currentPopup = signal<IReleaseNote | null>(null);
  public readonly releaseNote = signal<IReleaseNote | null>(null);

  private _popupQueue: IReleaseNote[] = [];

  public loadActiveDisplay(): Observable<{ banners: IReleaseNote[]; popups: IReleaseNote[] }> {
    return this._releaseNotesService.getActiveDisplay()
      .pipe(
        catchError(() => of({ banners: [], popups: [] })),
        tap(display => {
          const banners = (display?.banners ?? []).filter(note =>
            !this.isSessionDismissed(note.id, ReleaseNoteDisplayKind.BANNER)
          );
          const popups = (display?.popups ?? []).filter(note =>
            !this.isSessionDismissed(note.id, ReleaseNoteDisplayKind.POPUP)
          );

          this.banners.set(banners);
          this.releaseNote.set(banners[0] ?? null);
          this._popupQueue = [...popups];
          this.currentPopup.set(this._popupQueue.shift() ?? null);

          banners
            .filter(note => note.bannerFrequency === ReleaseNoteDisplayFrequency.FIRST_LOGIN)
            .forEach(note => this.markSeen(note.id, ReleaseNoteDisplayKind.BANNER));
        })
      );
  }

  public dismissBanner(note: IReleaseNote): void {
    if (note.bannerFrequency === ReleaseNoteDisplayFrequency.EVERY_LOGIN) {
      this.dismissForSession(note.id, ReleaseNoteDisplayKind.BANNER);
    }

    this.banners.update(items => items.filter(item => item.id !== note.id));
    this.releaseNote.set(this.banners()[0] ?? null);
  }

  public setReleaseNoteClosed(noteId: number): void {
    const note = this.banners().find(item => item.id === noteId);
    if (note) {
      this.dismissBanner(note);
    }
  }

  public dismissCurrentPopup(): void {
    const popup = this.currentPopup();
    if (!popup) {
      return;
    }

    if (popup.popupFrequency === ReleaseNoteDisplayFrequency.EVERY_LOGIN) {
      this.dismissForSession(popup.id, ReleaseNoteDisplayKind.POPUP);
    }

    if (popup.popupFrequency === ReleaseNoteDisplayFrequency.FIRST_LOGIN) {
      this.markSeen(popup.id, ReleaseNoteDisplayKind.POPUP);
    }

    this.currentPopup.set(this._popupQueue.shift() ?? null);
  }

  public reset(): void {
    this.banners.set([]);
    this.releaseNote.set(null);
    this._popupQueue = [];
    this.currentPopup.set(null);
  }

  public clearSessionDismiss(): void {
    sessionStorage.removeItem(ReleaseNotesStore.SessionStorageKey);
    this.reset();
  }

  private markSeen(id: number, kind: ReleaseNoteDisplayKind): void {
    this._releaseNotesService.markSeen(id, kind).subscribe({ error: () => undefined });
  }

  private isSessionDismissed(id: number, kind: ReleaseNoteDisplayKind): boolean {
    return this.readSessionDismissed().includes(this.sessionKey(id, kind));
  }

  private dismissForSession(id: number, kind: ReleaseNoteDisplayKind): void {
    const dismissed = this.readSessionDismissed();
    const key = this.sessionKey(id, kind);
    if (!dismissed.includes(key)) {
      dismissed.push(key);
      sessionStorage.setItem(ReleaseNotesStore.SessionStorageKey, JSON.stringify(dismissed));
    }
  }

  private readSessionDismissed(): string[] {
    try {
      const raw = sessionStorage.getItem(ReleaseNotesStore.SessionStorageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private sessionKey(id: number, kind: ReleaseNoteDisplayKind): string {
    return `${kind}:${id}`;
  }
}
